/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { BaseQueryFn } from "@reduxjs/toolkit/query/react"
import {
  axiosBaseQuery,
  AxiosBaseQueryArgs,
  AxiosBaseQueryError,
} from "queries/apiClient"
import {
  ECOMMERCE_MAIN_MERCHANT_ID_KEY,
  SELLER_CENTER_ERROR_NOTIFICATION_DURATION_SECONDS,
} from "ecommerce/constants"
import { getSellerCenterApiUrl } from "./constants"
import { getSellerCenterErrorNotificationContent } from "./sellerCenterApiError"

const innerBaseQuery = axiosBaseQuery(getSellerCenterApiUrl())

/**
 * Normalize the FE-only `ECOMMERCE_MAIN_MERCHANT_ID_KEY` ("main") sentinel —
 * used by merchantGroup-level users to represent the "all stores" selection —
 * back to an empty string, which is the value the BE expects.
 */
const normalizeMerchantIDInParams = (
  args: AxiosBaseQueryArgs,
): AxiosBaseQueryArgs => {
  if (args.params?.merchantID === ECOMMERCE_MAIN_MERCHANT_ID_KEY) {
    return { ...args, params: { ...args.params, merchantID: "" } }
  }
  return args
}

/**
 * Seller-center API base query: same as axiosBaseQuery for this service URL, with Mexcore-style
 * error notifications (dev_message, etc.) applied by default. Endpoints may override via
 * `extraOptions.getErrorNotificationContent`.
 *
 * Error toasts auto-dismiss after 10s unless the endpoint sets
 * `extraOptions.errorOptions.duration` (e.g. `0` to keep open).
 */
export const sellerCenterBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  AxiosBaseQueryError
> = (args, api, extra) => {
  const normalizedArgs = normalizeMerchantIDInParams(args)
  return innerBaseQuery(
    {
      ...normalizedArgs,
      extraOptions: {
        ...normalizedArgs.extraOptions,
        getErrorNotificationContent:
          normalizedArgs.extraOptions?.getErrorNotificationContent ??
          getSellerCenterErrorNotificationContent,
        errorOptions: {
          duration: SELLER_CENTER_ERROR_NOTIFICATION_DURATION_SECONDS,
          ...normalizedArgs.extraOptions?.errorOptions,
        },
      },
    },
    api,
    extra,
  )
}
